import { Component, HostListener } from '@angular/core';
import { RouterLink } from '@angular/router';
import { resultPairs } from '../../data/site-content';

interface LightboxImage {
  src: string;
  alt: string;
}

@Component({
  selector: 'atr-results',
  standalone: true,
  imports: [RouterLink],
  template: `
    <main class="page-shell">
      <section class="page-hero compact">
        <p class="section-kicker">Results</p>
        <h1>Before-and-after work that speaks plainly.</h1>
        <p>
          Real cleanup examples from interiors, doors, tar removal, foam washing, and exterior refreshes.
        </p>
      </section>

      <section class="proof section">
        <div class="comparison-grid">
          @for (pair of resultPairs; track pair.label) {
            <article class="compare-card">
              <div class="before-after">
                <figure>
                  <button class="image-zoom" type="button" (click)="openImage(pair.before, pair.beforeAlt)" aria-label="Open before image larger">
                    <img [src]="pair.before" [alt]="pair.beforeAlt" />
                  </button>
                  <figcaption>Before</figcaption>
                </figure>
                <figure>
                  <button class="image-zoom" type="button" (click)="openImage(pair.after, pair.afterAlt)" aria-label="Open after image larger">
                    <img [src]="pair.after" [alt]="pair.afterAlt" />
                  </button>
                  <figcaption>After</figcaption>
                </figure>
              </div>
              <div class="compare-copy">
                <span>{{ pair.label }}</span>
                <h3>{{ pair.title }}</h3>
              </div>
            </article>
          }
        </div>
      </section>

      <section class="gallery section" aria-label="Detailing work gallery">
        <div class="gallery-main">
          <button class="image-zoom" type="button" (click)="openImage('assets/foam-wash-front.jpg', 'White SUV covered in foam during hand wash')" aria-label="Open foam wash image larger">
            <img src="assets/foam-wash-front.jpg" alt="White SUV covered in foam during hand wash" />
          </button>
          <div class="gallery-caption">
            <span>Exterior work</span>
            <h2>Foam, hand work, rinse, and finish.</h2>
          </div>
        </div>
        <div class="gallery-side">
          @for (image of galleryImages; track image.src) {
            <button class="image-zoom" type="button" (click)="openImage(image.src, image.alt)" [attr.aria-label]="'Open ' + image.alt + ' larger'">
              <img [src]="image.src" [alt]="image.alt" />
            </button>
          }
        </div>
      </section>

      @if (selectedImage) {
        <div class="lightbox" role="dialog" aria-modal="true" aria-label="Expanded result image" (click)="closeImage()">
          <button class="lightbox-close" type="button" (click)="closeImage()" aria-label="Close expanded image">Close</button>
          <img [src]="selectedImage.src" [alt]="selectedImage.alt" (click)="$event.stopPropagation()" />
        </div>
      }

      <section class="cta-band">
        <h2>Ready to talk about your vehicle?</h2>
        <a class="button primary" routerLink="/book">Get in touch</a>
      </section>
    </main>
  `,
})
export class ResultsComponent {
  protected readonly resultPairs = resultPairs;
  protected readonly galleryImages: LightboxImage[] = [
    { src: 'assets/tar-process-1.jpg', alt: 'Tar removal process step one' },
    { src: 'assets/tar-process-2.jpg', alt: 'Tar removal process step two' },
    { src: 'assets/tar-process-3.jpg', alt: 'Tar removal process step three' },
    { src: 'assets/tar-process-4.jpg', alt: 'Tar removal process step four' },
  ];
  protected selectedImage: LightboxImage | null = null;

  protected openImage(src: string, alt: string): void {
    this.selectedImage = { src, alt };
    document.body.classList.add('lightbox-open');
  }

  protected closeImage(): void {
    this.selectedImage = null;
    document.body.classList.remove('lightbox-open');
  }

  @HostListener('window:keydown.escape')
  protected onEscape(): void {
    this.closeImage();
  }
}
